import { useCallback } from 'react'

import { useQuery } from '@tanstack/react-query'

import { API_URL } from '@/constants/env'

import { SweetspotError } from '@/helpers/error'

import type {
  LibrarySortDirection,
  LibrarySortOption,
} from '@/hooks/library/types'
import { libraryCatalogEntriesQueryKeys } from '@/hooks/library/types'
import { useAuthInfoWithThrow } from '@/hooks/user/useAuthInfoWithThrow'

import type { PaginateRequest, PaginatedResponse } from '@/types/Api'
import type { UUID } from '@/types/Id'
import type { EntityCatalogEntry } from '@/types/library/Entity'

interface UseCatalogEntriesArgs<T> {
  orgId: UUID | ''
  schemaId?: UUID | ''
  limit?: number
  offset?: number
  sort?: LibrarySortOption
  order?: LibrarySortDirection
  q?: string
  withConfirmed?: boolean
  withSuggested?: boolean
  enabled?: boolean
  select?: (data: PaginatedResponse<EntityCatalogEntry>) => T
}

export const useCatalogEntries = <T = EntityCatalogEntry[]>({
  orgId,
  schemaId,
  limit,
  offset,
  sort,
  order,
  q,
  withConfirmed,
  withSuggested,
  enabled = true,
  select,
}: UseCatalogEntriesArgs<T>) => {
  const { accessToken } = useAuthInfoWithThrow()

  const selectFn = useCallback(
    (data: PaginatedResponse<EntityCatalogEntry>) => {
      if (select) {
        return select(data)
      }
      return data as unknown as T
    },
    [select],
  )

  const pagination: PaginateRequest | undefined =
    limit !== undefined ? { limit, offset: offset ?? 0 } : undefined

  const queryKey = schemaId
    ? libraryCatalogEntriesQueryKeys.schema({
        orgId,
        schemaId,
        ...(pagination ? { limit, offset: offset ?? 0 } : {}),
        ...(sort ? { sort } : {}),
        ...(order ? { order } : {}),
        ...(q ? { q } : {}),
        withConfirmed,
        withSuggested,
      })
    : libraryCatalogEntriesQueryKeys.organization({
        orgId,
        ...(pagination ? { limit, offset: offset ?? 0 } : {}),
        ...(sort ? { sort } : {}),
        ...(order ? { order } : {}),
        ...(q ? { q } : {}),
        withConfirmed,
        withSuggested,
      })

  return useQuery({
    queryKey,
    queryFn: () =>
      fetchCatalogEntries(
        orgId,
        accessToken,
        schemaId,
        pagination,
        sort,
        order,
        q,
        withConfirmed,
        withSuggested,
      ),
    enabled: !!orgId && !!accessToken && enabled,
    staleTime: Infinity,
    placeholderData: (previousData) => previousData,
    select: selectFn,
  })
}

interface UseSchemaEntriesArgs<T> {
  orgId: UUID | ''
  schemaId: UUID | ''
  select?: (data: PaginatedResponse<EntityCatalogEntry>) => T
}

export const useSchemaEntries = <T = EntityCatalogEntry[]>({
  orgId,
  schemaId,
  select,
}: UseSchemaEntriesArgs<T>) => {
  const { accessToken } = useAuthInfoWithThrow()

  const selectFn = useCallback(
    (data: PaginatedResponse<EntityCatalogEntry>) => {
      if (select) {
        return select(data)
      }
      return data.data as unknown as T
    },
    [select],
  )

  return useQuery({
    queryKey: libraryCatalogEntriesQueryKeys.schema({ orgId, schemaId }),
    queryFn: () => fetchCatalogEntries(orgId, accessToken, schemaId),
    enabled: !!orgId && !!accessToken && !!schemaId,
    staleTime: Infinity,
    select: selectFn,
  })
}

export const fetchCatalogEntries = async (
  orgId: UUID | '',
  accessToken: string,
  schemaId?: UUID | '',
  pagination?: PaginateRequest,
  sort?: LibrarySortOption,
  order?: LibrarySortDirection,
  q?: string,
  withConfirmed?: boolean,
  withSuggested?: boolean,
): Promise<PaginatedResponse<EntityCatalogEntry>> => {
  if (!orgId) {
    throw new Error('orgId is required')
  }
  const params = new URLSearchParams()
  if (schemaId) {
    params.append('schema_id', schemaId)
  }
  if (sort) {
    params.append('sort', sort)
  }
  if (order) {
    params.append('order', order)
  }
  if (q) {
    params.append('q', q)
  }
  if (typeof withConfirmed === 'boolean') {
    params.append('with_confirmed', String(withConfirmed))
  }
  if (typeof withSuggested === 'boolean') {
    params.append('with_suggested', String(withSuggested))
  }
  if (pagination) {
    if (pagination.limit) {
      params.append('limit', pagination.limit.toString())
    }
    if (pagination.offset !== undefined) {
      params.append('offset', pagination.offset.toString())
    }
  }

  const path = `${API_URL}/api/v1/organizations/${orgId}/entities/catalog-entries?${params.toString()}`

  const response = await fetch(path, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    },
  })
  if (!response.ok) {
    throw new SweetspotError({
      name: 'FETCH_ERROR',
      message: response.statusText,
      cause: response,
    })
  }
  const result =
    (await response.json()) as PaginatedResponse<EntityCatalogEntry>

  return result
}

interface UseCatalogEntryArgs {
  orgId: UUID | ''
  catalogEntryId: UUID | ''
}

const fetchCatalogEntry = async (
  orgId: UUID | '',
  catalogEntryId: UUID | '',
  accessToken: string,
): Promise<EntityCatalogEntry> => {
  if (!orgId) {
    throw new Error('orgId is required')
  }
  if (!catalogEntryId) {
    throw new Error('catalogEntryId is required')
  }
  const response = await fetch(
    `${API_URL}/api/v1/organizations/${orgId}/entities/${catalogEntryId}`,
    {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
    },
  )
  if (!response.ok) {
    throw new SweetspotError({
      name: 'FETCH_ERROR',
      message: response.statusText,
      cause: response,
    })
  }
  return (await response.json()) as EntityCatalogEntry
}

export const useCatalogEntry = ({
  orgId,
  catalogEntryId,
}: UseCatalogEntryArgs) => {
  const { accessToken } = useAuthInfoWithThrow()
  return useQuery({
    queryKey: libraryCatalogEntriesQueryKeys.entry({ orgId, catalogEntryId }),
    queryFn: () => fetchCatalogEntry(orgId, catalogEntryId, accessToken),
    enabled: !!orgId && !!catalogEntryId && !!accessToken,
    staleTime: Infinity,
  })
}
